import React, { useContext } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { createPatch } from 'rfc6902';
import createErrorMessage from '../helpers/errorMessages';
import { ApiContext } from '../context/ApiContext';

const useCreateUpdate = <ObjectType>(
  mode: string,
  translationKey: string,
  endpoint: string,
  sentryMsg: string,
  cacheKey: string,
  resetObject: () => void,
  removeSavedObject: () => void,
  setSuccessMessage: (message: string) => void,
  setErrorMessage: (message: string) => void,
  setErrors: (obj: any) => void,
  objectId: string
) => {
  const { t, i18n } = useTranslation();
  const { apiFetch } = useContext(ApiContext);
  const queryClient = useQueryClient();

  const apiFunction = (object: ObjectType | { oldObject: ObjectType; newObject: ObjectType }) => {
    if (mode === 'create') {
      return apiFetch(`${endpoint}`, object, 'POST');
    }

    const { oldObject, newObject } = object as { oldObject: ObjectType; newObject: ObjectType };
    const patchData = createPatch(oldObject, newObject);
    return apiFetch(`${endpoint}/${objectId}`, patchData, 'PATCH', {
      'Content-Type': 'application/json-patch+json',
    });
  };

  const { isLoading, mutate: apiCall } = useMutation((object: ObjectType) => apiFunction(object), {
    async onSuccess() {
      await queryClient.invalidateQueries([cacheKey]);
      resetObject();
      removeSavedObject();

      setSuccessMessage(
        t(`screens.${translationKey}.${mode === 'create' ? 'successCreate' : 'successUpdate'}`)
      );
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError(error: any) {
      if (error?.code === 401) return;
      Sentry.captureException(
        new Error(`Error ${mode === 'create' ? 'creating' : 'updating'} ${sentryMsg}`),
        {
          extra: { error: JSON.stringify(error) },
        }
      );

      const errorMessage = createErrorMessage(
        error,
        t,
        i18n,
        `screens.${translationKey}.${mode === 'create' ? 'errorCreating' : 'errorUpdating'}`
      );

      setErrorMessage(errorMessage);

      const errorFields = error.errors?.fields;
      if (!errorFields) {
        setErrors({});
        return;
      }
      Object.keys(errorFields).forEach((key) => {
        const err = errorFields[key];
        let message = '';
        err.forEach((element: { key: string; message: string; params?: string[] }) => {
          const errorParams = element?.params?.reduce(
            (acc, item, index) => ({ ...acc, [`param${index}`]: item }),
            {}
          );
          message += `${t(`errors.${element.key}`, element.message, errorParams)} `;
        });

        errorFields[key] = message;
      });
      setErrors(errorFields);
    },
  });

  return { isLoading, apiCall };
};
export default useCreateUpdate;
